/**
 * feat-270 — the approval queue's selection, and the predicate that decides
 * whether approving it is confirmed. Spec **AT-27d**, `design/NOTES.md` §6b,
 * queue state K.
 *
 * Pure and free of React on purpose: AT-27d is the one acceptance criterion in
 * this slice whose failing arms are BOTH invisible in a rendered screenshot, so
 * it wants a unit test that can drive the state machine directly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PREDICATE IS PROVENANCE, NOT SIZE.
 *
 * `selectedCount === totalCount` is the intuitive predicate and it is wrong in
 * BOTH directions:
 *
 *   - it FIRES when someone ticks all 26 boxes by hand — 26 deliberate acts,
 *     which are precisely the deliberation the dialog exists to substitute for;
 *   - it is SILENT on select-all-then-untick-one, which publishes 25 lessons on
 *     three clicks.
 *
 * AT-27d pins both arms, "because a test that only asserts '26 selected →
 * confirm shown' passes on the wrong predicate". So the flag below is set by the
 * select-all CONTROL and never inferred from a count.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ A CONTRADICTION IN THE SOURCES, RESOLVED TOWARD THE SPEC. See QUESTIONS.md
 * Q9.
 *
 * `NOTES.md` §6b says: *"the confirm fires when the selection was produced by the
 * select-all control **and has not been narrowed since**. Unticking anything
 * makes it hand-picked again."* Read literally, select-all-then-untick-one does
 * NOT confirm.
 *
 * `spec.md` AT-27d says the opposite, in terms, and makes it a required arm:
 * *"**select-all then untick one** must **still** confirm — otherwise three
 * clicks publish 25 lessons."* `FRONTEND-SLICE.md` §9.4 agrees with the spec: it
 * lists "stays silent on select-all-then-untick-one" among the FAILURES of the
 * naive predicate.
 *
 * The spec is approved, it is the later document, and it enumerates the arm
 * explicitly rather than in passing — so `fromSelectAll` is **STICKY under
 * narrowing**: unticking a row keeps the confirm armed. It is cleared only by
 * emptying the selection (Clear, or unticking the last row), after which any
 * selection is hand-built again.
 *
 * The cost of being wrong this way is one extra dialog on a narrowed select-all.
 * The cost of being wrong the other way is 25 lessons published on three clicks,
 * which is the hazard the whole control exists for.
 */

import type { ApprovalLessonDetail } from '../../../services/curriculumApprovalService';

/**
 * The fields whose change is an answer-key change.
 *
 * ONE definition, two consumers, and `NOTES.md` §6b says so explicitly: this is
 * the same set §5 sorts to the top of every item, and *"if it is ever edited, it
 * must be edited in one place."* The two consumers are `answerKeyFieldRank`
 * (ordering, NOTES §5) and `summarizeSelection` (the confirm dialog's second
 * sentence, NOTES §6b).
 */
export const ANSWER_KEY_FIELDS: ReadonlySet<string> = new Set([
  'correct_option',
  'blank_accepted_variants',
]);

/**
 * The selection, with the one bit of history that the size cannot carry.
 *
 * `fromSelectAll` is not derivable from `ids` — that is the entire point. Two
 * identical `ids` sets, one built by clicking Select all and one built by
 * ticking every row, must behave differently.
 */
export interface ApprovalSelection {
  readonly ids: ReadonlySet<string>;
  /** True iff this selection descends from a Select-all click. Never inferred. */
  readonly fromSelectAll: boolean;
}

export const EMPTY_SELECTION: ApprovalSelection = {
  ids: new Set<string>(),
  fromSelectAll: false,
};

/** The Select-all control. This is the ONLY place `fromSelectAll` becomes true. */
export function selectAll(allLessonIds: readonly string[]): ApprovalSelection {
  return { ids: new Set(allLessonIds), fromSelectAll: true };
}

/** The Clear control, and the only reset. */
export function clearSelection(): ApprovalSelection {
  return EMPTY_SELECTION;
}

/**
 * A per-row checkbox.
 *
 * Narrowing a select-all selection KEEPS `fromSelectAll` (AT-27d's second arm).
 * Emptying it drops the flag, because an empty selection has no provenance worth
 * carrying and whatever is built next is hand-built.
 */
export function toggleLesson(
  selection: ApprovalSelection,
  lessonId: string,
): ApprovalSelection {
  const ids = new Set(selection.ids);
  if (ids.has(lessonId)) {
    ids.delete(lessonId);
  } else {
    ids.add(lessonId);
  }
  if (ids.size === 0) return EMPTY_SELECTION;
  return { ids, fromSelectAll: selection.fromSelectAll };
}

/**
 * AT-27d. **The confirm predicate.**
 *
 * Note what is absent: any reference to `ids.size`, and any reference to a total.
 * If a future edit introduces one, that is the bug this file was written to
 * prevent.
 */
export function requiresConfirm(selection: ApprovalSelection): boolean {
  return selection.fromSelectAll && selection.ids.size > 0;
}

/** What the confirm dialog names — the two subsets a select-all selection hides. */
export interface SelectionSummary {
  lessonCount: number;
  /** Sum of `pending_item_count` — the exact number, read from the server. */
  changeCount: number;
  /** Lessons reaching employees for the first time. Exact: `state` is per lesson. */
  neverApproved: ApprovalLessonDetail[];
  /** Lessons with at least one changed answer-key field among their RENDERED items. */
  answerKeyLessons: ApprovalLessonDetail[];
  /**
   * True when any selected lesson's item list was truncated (`items_truncated`,
   * ADR-B §3.2 — `item_limit` defaults to 10 per lesson).
   *
   * `answerKeyLessons` is then a **floor**, not a count: an answer-key change in
   * a lesson's 11th pending item is real and is not in `items`. The dialog says
   * "at least N" in that case rather than asserting a number it cannot support —
   * understating the rogue vector in the one dialog written to name it would be
   * worse than a slightly hedged sentence. `neverApproved` is unaffected, since
   * `state` rides on the lesson rather than on its items.
   *
   * This is the case that is NORMAL rather than exotic: the first approval of a
   * fresh tenant makes every item `"created"`, so most lessons truncate at 10.
   */
  answerKeyCountIsFloor: boolean;
  /** The selected lessons, in the order the queue rendered them. */
  lessons: ApprovalLessonDetail[];
}

/**
 * Both dialog counts, from data already on screen — no new endpoint, no new
 * field (NOTES §6b).
 *
 * ⚠ This reads the server's `fields[].changed` and never compares two values.
 * A field counts as an answer-key change when the SERVER marked it changed and
 * its name is in `ANSWER_KEY_FIELDS`; whether the two values differ is not this
 * module's question and must not become one (ADR-B §2.1).
 */
export function summarizeSelection(
  selection: ApprovalSelection,
  allLessons: readonly ApprovalLessonDetail[],
): SelectionSummary {
  const lessons = allLessons.filter((l) => selection.ids.has(l.lesson_id));
  const neverApproved = lessons.filter((l) => l.state === 'never_approved');
  const answerKeyLessons = lessons.filter((l) =>
    l.items.some((item) =>
      item.fields.some((f) => f.changed && ANSWER_KEY_FIELDS.has(f.field)),
    ),
  );
  return {
    lessonCount: lessons.length,
    changeCount: lessons.reduce((sum, l) => sum + l.pending_item_count, 0),
    neverApproved,
    answerKeyLessons,
    answerKeyCountIsFloor: lessons.some((l) => l.items_truncated),
    lessons,
  };
}

/**
 * NOTES §5 rule 1 — answer-affecting fields sort FIRST in an item's field list,
 * above every other changed field, regardless of the panel's own field order.
 *
 * The rogue edit (AT-13) is a one-character diff and next to a rewritten
 * paragraph it is visually nothing. **Ordering is what does that work, not
 * colour** — a red tint on an answer-key change would be wrong, because the
 * editor may well be correcting a genuine error and `danger-*` is reserved for a
 * fault the author cannot fix.
 *
 * Stable within each rank: `Array.prototype.sort` is stable in every engine this
 * app targets, so the server's field order survives inside a rank.
 */
export function sortFieldsAnswerKeyFirst<T extends { field: string }>(
  fields: readonly T[],
): T[] {
  return [...fields].sort(
    (a, b) => answerKeyFieldRank(a.field) - answerKeyFieldRank(b.field),
  );
}

function answerKeyFieldRank(field: string): number {
  return ANSWER_KEY_FIELDS.has(field) ? 0 : 1;
}
